import * as React from 'react';
import { Typography, Container, Grid, Paper, Button, Tabs, Tab } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';

import { useStyles } from './styles';
import ApplicantDetails from 'components/QuotationResume/ApplicantDetails/ApplicantDetails';
import QuoteResult from 'components/QuotationResume/QuoteResult/QuoteResult';
import {
  getQuotationResume,
  getInsureStatus,
  resetBotQuotation,
  getInsureStatusClient,
} from 'services/QuotationService';
import { formatDate, createDataList as createQuotationData } from 'utils/utils';
import { cities } from 'pages/CarsQuotation/cities';
import QuoteResultClient from 'components/QuotationResume/QuoteResultClient/QuoteResultClient';

type TNewQuotationValue = { [Key in symbol]?: object };

const QuotationResume = () => {
  // classes
  const classes = useStyles();

  // states
  const [quotationValue, setQuotation] = React.useState<IQuoteResponse | null>(null);
  const [userData, setUserData] = React.useState<IApplicantTableData[]>([]);
  const [quotationClient, setQuotationClient] = React.useState([]);
  const [tabValue, setTabValue] = React.useState(0);

  // navigation
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const routerNavigationToCarsQuotation = (newQuotationValue?: TNewQuotationValue) => {
    if (newQuotationValue) {
      history.push({
        pathname: '/app/cotizador/autos',
        state: {
          quotation: newQuotationValue,
        },
      });
    } else {
      history.push({
        pathname: '/app/cotizador/autos',
      });
    }
  };

  // handlers
  const onNavigationToReQuote = () => {
    const selectedInsurerSelect = quotationValue?.selectedInsurers.map((quote) => ({
      value: quote,
      label: quote,
    }));

    const cityCirculationSearch = cities.find(
      (city: { value: string; label: string }) => city.value === quotationValue?.cityCirculation[0]
    );

    const newQuotationValue: TNewQuotationValue = {
      ...quotationValue,
      selectedInsurers: selectedInsurerSelect,
      cityCirculation: cityCirculationSearch,
    };
    routerNavigationToCarsQuotation(newQuotationValue);
  };

  const onNavigationToReQuoteWithEmptyForm = () => {
    routerNavigationToCarsQuotation();
  };

  const responseQuoteService = React.useCallback(async () => {
    const typeQuoteWithId = `${id}/auto`;
    const { cotizacion } = await getQuotationResume(typeQuoteWithId);
    const collectionQuotationData = [
      createQuotationData('Nombre', `${cotizacion.firstName} ${cotizacion.lastName}`),
      createQuotationData('Documento', `${cotizacion.nDocument}`),
      createQuotationData('Nacimiento', cotizacion.fBorn),
      createQuotationData('Valor Asegurado', `${cotizacion.valueInsured}`),
      createQuotationData('Estado Civil', cotizacion.civilState),
      createQuotationData('Género', cotizacion.gender),
      createQuotationData('Placa', cotizacion.licenseVehicle),
      createQuotationData('Modelo', `${cotizacion.modelo}`),
      createQuotationData('Código Fasecolda', cotizacion.codeFasecolda),
      createQuotationData('Marca', cotizacion.brand),
      createQuotationData('Ref 1', cotizacion.ref1),
      createQuotationData('Ref 2', cotizacion.ref2),
      createQuotationData('Ref 3', cotizacion.ref3),
      createQuotationData('Ciudad', cotizacion.cityCirculation.join(', ')),
      createQuotationData('Servicio', cotizacion.service),
      createQuotationData('Tipo Vehiculo', cotizacion.tVehicle),
    ];

    setUserData(collectionQuotationData);

    setQuotation(cotizacion);
  }, [id]);

  const onInsureStatusData = React.useCallback(
    async (insurerName?: string) => {
      const response = await getInsureStatus(id, insurerName);
      return response;
    },
    [id]
  );

  const onResetBotQuotation = React.useCallback(
    async (insurer?: string) => {
      if (insurer) {
        const response = await resetBotQuotation(id, insurer);

        return response;
      }
    },
    [id]
  );

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  React.useEffect(() => {
    if (tabValue === 1) {
      const fetchProducts = async () => {
        const { filteredProductsQuotations } = await getInsureStatusClient(id);
        setQuotationClient(filteredProductsQuotations);
      };
      fetchProducts();
    }
  }, [tabValue]);

  React.useEffect(() => {
    responseQuoteService();
  }, [responseQuoteService]);

  React.useEffect(() => {
    document.title = 'Resumen de la cotización';
  }, []);

  return (
    <>
      <Grid item xs={12}>
        <Container maxWidth="xl" style={{ marginTop: '50px' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="cotización tabs"
            variant="fullWidth">
            <Tab label="Asesor" style={{ fontWeight: 'bold' }} />
            <Tab label="Cliente" style={{ fontWeight: 'bold' }} />
          </Tabs>
        </Container>
      </Grid>
      {tabValue === 0 && (
        <>
          <Grid item xs={12} md={9} lg={9}>
            <Container className={classes.headerTitle} maxWidth="xl">
              <Typography variant="h4" gutterBottom className={classes.title} display="block">
                Número de Cotización: {quotationValue?.consecutiveTotalNumber}
              </Typography>
              <Typography
                variant="caption"
                gutterBottom
                display="block"
                className={classes.dateLabel}>
                {formatDate(quotationValue?.createdAt)}
              </Typography>
            </Container>
          </Grid>
          <Grid item xs={12} md={3} lg={3} className={classes.quoteAgain}>
            <Container maxWidth="xl">
              <Button
                color="primary"
                variant="contained"
                className={classes.fullWidthButton}
                onClick={onNavigationToReQuote}>
                Cotizar de nuevo
              </Button>
              <Button
                color="primary"
                variant="contained"
                className={classes.fullWidthButton}
                onClick={onNavigationToReQuoteWithEmptyForm}>
                Nueva cotización
              </Button>
            </Container>
          </Grid>
          <Grid item xs={12} md={7} lg={9}>
            <Paper className={classes.paper}>
              <QuoteResult
                insuresResults={quotationValue}
                onInsureStatusData={onInsureStatusData}
                onResetBotQuotation={onResetBotQuotation}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={5} lg={3}>
            <Paper className={classes.paper}>
              <ApplicantDetails tableData={userData} />
            </Paper>
          </Grid>
        </>
      )}

      {tabValue === 1 && (
        <QuoteResultClient
          filteredProductsQuotations={quotationClient || []}
          key={quotationClient?.length}
        />
      )}
    </>
  );
};

export default QuotationResume;
